import { Helmet } from 'react-helmet-async';

import { useEffect, useRef } from 'react';

// Utils
import { lazyWithRetry } from 'utils/DynamicContent';

// Hooks
import { useStores } from 'hooks/use-stores';

// Constants
import { SITE_META_DATA } from 'constants/SEO';

import { FullScreenLayout, PaddedContent } from './styles';

const DefaultFooter = lazyWithRetry(async () => await import('components/footers/DefaultFooter'));
const SimpleHeader = lazyWithRetry(async () => await import('components/headers/SimpleHeader'));

const Page = ({
  className,
  children,
  HeaderComponent,
  FooterComponent,
  showHeader = true,
  showFooter = true,
  'aria-label': ariaLabel = '',
  ...props
}) => {
  const { hostStore } = useStores();

  const seo = {
    title: props.title || SITE_META_DATA.DEFAULT_TITLE,
    description: props.description || SITE_META_DATA.DEFAULT_DESCRIPTION,
    url: window.location,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    titleTemplate: `%s | ${hostStore.settings?.title || 'DoorDash'}`,
    ogImage: props.ogImage || SITE_META_DATA.DEFAULT_IMAGE,
    index: `${props.noIndex === true ? 'noindex' : 'index'}`,
    follow: `${props.noFollow === true ? 'nofollow' : 'follow'}`,
    // favicon is set in App.js on an app wide level
  };

  const mainRef = useRef<HTMLBodyElement>(null);

  useEffect(() => {
    // A11Y: On render focus main. This is needed when routing between pages. It is important to only focus
    // if an ariaLabel is provided, we only want to switch focus on pages that aren't focusing something else
    if (ariaLabel !== '' && mainRef.current) {
      mainRef.current.focus();
    }
  }, []);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <main aria-label={ariaLabel} tabIndex={0} ref={mainRef}>
      <FullScreenLayout className={className}>
        <Helmet title={seo.title} titleTemplate={seo.titleTemplate}>
          <meta name="description" content={seo.description} />
          {/* https://ahrefs.com/blog/open-graph-meta-tags/#which-open-graph-tags-to-use */}
          <meta property="og:title" content={seo.title} />
          <meta property="og:description" content={seo.description} />
          {/* NOTE: This should be the canonical URL */}
          <meta property="og:url" content={seo.url} />
          <meta property="og:image" content={seo.image} />
          <meta property="og:type" content="website" />
          <meta name="robots" content={`${seo.index}, ${seo.follow}`} />
        </Helmet>

        {showHeader && (HeaderComponent || <SimpleHeader />)}
        <PaddedContent>{children}</PaddedContent>
        {showFooter && (FooterComponent || <DefaultFooter />)}
      </FullScreenLayout>
    </main>
  );
};

export default Page;
